import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "../../annotations";
import { Page } from ".";
import { AppSetting, AppSettingExtraFields, AppSettingPermission } from "./dtos/AppSetting";

const APP_SETTINGS_SERVICE = `${Constants.API_URL}/bedrock/app_settings`;
const APP_SETTING_PERMISSIONS_SERVICE = `${Constants.API_URL}/bedrock/app_setting_permissions`;

export interface AppSettingPermissionUpdate {
  appSettingId: string;
  accountId: number;
  value: boolean | null;
}

type AccountType = "coach" | "player";
type ExtraField = keyof AppSettingExtraFields;
type ExtraFields = ExtraField[] | ExtraField;
export interface AppSettingFilters {
  isPlayerSetting?: boolean;
  extraFields?: ExtraFields;
  page?: number;
  perPage?: number;
}

export interface AppSettingPermissionsSummaryParams {
  accountType: AccountType;
  accountId: number;
}

export interface AppSettingPermissionFilters {
  accountId?: number;
  appSettingId?: string;
  page?: number;
  perPage?: number;
}

export class AppSettingServices {
  @scCatchServiceException()
  static async getAppSettings(params: AppSettingFilters = {}): Promise<Page<AppSetting>> {
    const url = APP_SETTINGS_SERVICE;
    params.page = params.page ?? 1;
    params.perPage = params.perPage ?? 100;
    const { data } = await axios.get<Page<AppSetting>>(url, { params });
    return data;
  }

  @scCatchServiceException()
  static async getAppSetting(appSettingId: string, extraFields?: ExtraFields): Promise<AppSetting> {
    const url = `${APP_SETTINGS_SERVICE}/${appSettingId}`;
    const { data } = await axios.get<AppSetting>(url, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static getPlayerSettingPermissions(filters: AppSettingPermissionFilters = {}): Promise<Page<AppSettingPermission>> {
    return this.getAppSettingPermissions("player", filters);
  }

  @scCatchServiceException()
  static getCoachSettingPermissions(filters: AppSettingPermissionFilters = {}): Promise<Page<AppSettingPermission>> {
    return this.getAppSettingPermissions("coach", filters);
  }

  @scCatchServiceException()
  static async getAppSettingPermissions(
    accountType: AccountType,
    filters: AppSettingPermissionFilters = {}
  ): Promise<Page<AppSettingPermission>> {
    const params = {
      page: 1,
      perPage: 100,
      accountType,
      ...filters,
    };
    const { data } = await axios.get<Page<AppSettingPermission>>(APP_SETTING_PERMISSIONS_SERVICE, { params });
    return data;
  }

  @scCatchServiceException()
  static async getAppSettingPermissionsSummary(
    params: AppSettingPermissionsSummaryParams | undefined = undefined
  ): Promise<Record<string, boolean>> {
    const { data } = await axios.get<Record<string, boolean>>(`${APP_SETTING_PERMISSIONS_SERVICE}/summary`, { params });
    return data;
  }

  @scCatchServiceException()
  static updatePlayerSettingPermissions(permissions: AppSettingPermissionUpdate[]): Promise<void> {
    return this.updateAppSettingPermissions("player", permissions);
  }

  @scCatchServiceException()
  static updateCoachSettingPermissions(permissions: AppSettingPermissionUpdate[]): Promise<void> {
    return this.updateAppSettingPermissions("coach", permissions);
  }

  @scCatchServiceException()
  static async updateAppSettingPermissions(
    accountType: AccountType,
    permissions: AppSettingPermissionUpdate[]
  ): Promise<void> {
    await axios.patch<void>(APP_SETTING_PERMISSIONS_SERVICE, { accountType, permissions });
  }
}
